import React, { useState, ChangeEvent } from "react";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tutorial } from "@/assets";
import { applyMask } from "@/utils/maskUtils";

interface StringFieldProps {
  label: string;
  name: string;
  value: string;
  onChange: (name: string, value: string) => void;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  tutorial?: string;
  onBlur?: (name: string) => void;
  touched?: boolean;
  error?: string;
  mask?: string;
  isPassword?: boolean;
  description?: string;
  videoUrl?: string;
}

export const StringField: React.FC<StringFieldProps> = ({
  label,
  name,
  value = "",
  onChange,
  required = false,
  placeholder,
  disabled = false,
  tutorial,
  onBlur,
  touched,
  error,
  mask,
  isPassword = false,
  description,
  videoUrl,
}) => {
  console.log(`🎯 StringField - Received props:`, {
    name,
    value,
    valueType: typeof value,
    label,
    mask,
    isPassword
  });
  
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    let newValue = e.target.value;
    if (mask) {
      newValue = applyMask(newValue, mask);
    }
    onChange(name, newValue);
  };

  const handleBlur = () => {
    if (onBlur) {
      onBlur(name);
    }
  };

  return (
    <>
      <h1 className="text-background font-bold text-sm sm:text-2xl mb-2.5 sm:mb-4">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </h1>
      {description && (
        <h2 className="text-grey-light text-sm sm:text-base mb-6 sm:mb-8">
          {description}
        </h2>
      )}

      {mask && (
        <p className="text-grey-light text-xs sm:text-sm mb-4 italic">
          Formato: {mask}
        </p>
      )}

      <div className="flex flex-col justify-center text-xs sm:text-sm gap-4 w-full">
        <div className="relative">
          <Input
            type={isPassword && !showPassword ? "password" : "text"}
            id={name}
            name={name}
            value={value || ""}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={placeholder}
            disabled={disabled}
            className="placeholder:text-background/60 border-0 border-b border-b-background rounded-none focus-visible:ring-0"
          />
          {isPassword && (
            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="text-white/70 hover:text-white"
              >
                {showPassword ? (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M10.79 10.79L9.83 9.83C9.24 10.42 8.42 10.75 7.5 10.75C5.84 10.75 4.5 9.41 4.5 7.75C4.5 6.83 4.83 6.01 5.42 5.42L4.46 4.46C3.69 5.23 3.25 6.25 3.25 7.75C3.25 10.1 5.15 12 7.5 12C8.75 12 9.77 11.56 10.54 10.79H10.79Z"
                      fill="currentColor"
                    />
                    <path
                      d="M7.5 4.25C9.16 4.25 10.5 5.59 10.5 7.25C10.5 8.17 10.17 8.99 9.58 9.58L10.54 10.54C11.31 9.77 11.75 8.75 11.75 7.25C11.75 4.9 9.85 3 7.5 3C6.25 3 5.23 3.44 4.46 4.21L5.42 5.17C6.01 4.58 6.83 4.25 7.5 4.25Z"
                      fill="currentColor"
                    />
                    <path
                      d="M1.5 1.5L13.5 13.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                ) : (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8 3C5.65 3 3.75 4.9 3.75 7.25C3.75 9.6 5.65 11.5 8 11.5C10.35 11.5 12.25 9.6 12.25 7.25C12.25 4.9 10.35 3 8 3ZM8 9.75C6.34 9.75 5 8.41 5 6.75C5 5.09 6.34 3.75 8 3.75C9.66 3.75 11 5.09 11 6.75C11 8.41 9.66 9.75 8 9.75Z"
                      fill="currentColor"
                    />
                    <path
                      d="M8 5.5C7.17 5.5 6.5 6.17 6.5 7C6.5 7.83 7.17 8.5 8 8.5C8.83 8.5 9.5 7.83 9.5 7C9.5 6.17 8.83 5.5 8 5.5Z"
                      fill="currentColor"
                    />
                  </svg>
                )}
              </button>
            </div>
          )}
        </div>

        {touched && error && (
          <div className="text-red-500 text-sm mt-2">{error}</div>
        )}
      </div>

      {videoUrl && (
        <Dialog>
          <DialogTrigger asChild>
            <Button
              variant="ghost"
              className="flex gap-2 items-center bg-background/10 rounded-md w-fit px-4 py-2 mt-4"
            >
              <Tutorial fill="var(--background)" />
              <span>Ver vídeo explicativo</span>
            </Button>
          </DialogTrigger>
          <DialogContent className="aspect-video min-w-2/3 bg-black/90 rounded-2xl border border-gray-800 text-grey-light">
            <div
              style={{
                left: 0,
                width: "100%",
                height: 0,
                position: "relative",
                paddingBottom: "56.25%",
              }}
            >
              <iframe
                src={videoUrl}
                style={{
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  position: "absolute",
                  border: 0,
                }}
                allowFullScreen
                scrolling="no"
                allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share;"
              />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};